import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { LAYOUT_ANIMATIONS } from './layout.animations';
import { AppInsightsService, ConfigurationService, ExportsService, SpinnerService } from '../_shared/services/services-index';
import { Enums, IEnums } from '../_shared/enums/enums';
import { LayoutHelperService } from './layoutHelper.service';
import { Router, NavigationStart } from '@angular/router';
import * as Models from '../_shared/models/models-index';
import { Store, select } from '@ngrx/store';
import { States } from '../_store/app.reducer';
import { selectLoggedInUser } from '../_store/app.selectors';
import { AppSelectors } from '../_store/selector-types';
import { map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

import { jsPDF } from 'jspdf';
import html2canvas, { Options } from 'html2canvas';
import domtoimage from 'dom-to-image';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss',
    animations: LAYOUT_ANIMATIONS,
    standalone: false
})
export class LayoutComponent implements OnInit, OnDestroy {

  @ViewChild('printArea') printArea: ElementRef;

  public loading$: Observable<boolean>;
  subscriptions: Subscription[] = [];

  secondaryNavState = 'open';
  defaultSidebarState: 'open' | 'closed' = !!environment.sidebarDefaultOpen ? 'closed' : 'open'; // this is reversed intentionally so it works right
  enums: IEnums = Enums;
  authInfo: Models.IAuthenticationInfo;
  authInfo$ = this.store$.pipe(select(selectLoggedInUser));
  currentRouteData$ = this.store$.pipe(select(AppSelectors.selectCurrentRouteData));
  showFilterBar$: Observable<boolean>;
  showReportFilterBar$: Observable<boolean>;
  showAnyFilterBar$: Observable<boolean>;
  showBreadcrumb$ = this.currentRouteData$.pipe(
    map((routeData) => !routeData.hideBreadcrumbs)
  );

  constructor(
    private store$: Store<States.AppState>,
    private configService: ConfigurationService,
    public layoutHelper: LayoutHelperService,
    private exportsService: ExportsService,
    private spinnerService: SpinnerService,
    private appInsights: AppInsightsService,
    private router: Router) {
  }

  ngOnInit() {
    this.subscriptions.push(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.layoutHelper.toggleSidebar(this.defaultSidebarState);
          this.layoutHelper.ensureMobileSidebarExpanded();
        }
      }),
      this.authInfo$.subscribe(authInfo => this.authInfo = authInfo),
      this.exportsService.exportButtonClicked$.subscribe(printingOption => {
        if (printingOption === this.enums.printingOptions.pdf) {
          this.printToPdf();
        }
      }),
    );
    this.layoutHelper.toggleSidebar(this.defaultSidebarState);
  this.syncSidebarForViewport();
    this.layoutHelper.updateCurrentCategory();

    this.showReportFilterBar$ = this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      map(routeData => {
        return !!routeData.reportName;
      })
    );

    this.showAnyFilterBar$ = this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      map(routeData => {
        return !!routeData.reportName;
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncSidebarForViewport();
  }

  private syncSidebarForViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.innerWidth < 992) {
      this.layoutHelper.ensureMobileSidebarExpanded();
    }
  }

  printToPdf() {
    this.spinnerService.show();
    setTimeout(() => {
      this.generatePdf();
    }, 200);
  }

  generatePdf() {
    //Grab existing/current element heights
    const navbarHeight = document.getElementById('app-navbar').clientHeight;//.style.height;
    const breadCrumbHeight = document.getElementById('app-breadcrumb').clientHeight;
    const filterBarHeight = document.getElementById('app-filterbar').clientHeight;
    const footerHeight = document.getElementById('app-footer').clientHeight;
    const topOffset = navbarHeight + breadCrumbHeight + filterBarHeight;
    const newHeight = Math.max(window.screen.height, 1700);

    //Resize the main report area height
    document.getElementById('app-body').style.height = `${newHeight}px`;
    document.getElementById('main-content').style.height = `${newHeight - topOffset - 8}px`;
    document.getElementById('sidebar-container').style.height = `${newHeight - navbarHeight - footerHeight}px`;

    const div = document.body;
    const options = {
      filter: (element: Element) => element.id != 'spinner'
    };

    domtoimage.toPng(div, options).then((dataUrl) => {
      html2canvas(div).then(canvas => {
        const aspectRatio = canvas.width / canvas.height;
        const doc = new jsPDF('p', 'in', 'letter');
        doc.addImage(dataUrl, 'JPEG', 0, 0, 8.5, 8.5 / aspectRatio);

        this.store$.select(AppSelectors.selectCurrentRouteData).pipe(take(1)).subscribe(routeData => {
          const reportName = routeData.reportName || 'Report';
          doc.save(`${reportName}.pdf`);
        },
        err => {
          this.appInsights.trackException(err)
          throw new Error(err);
        })
      }).catch(err => {
        this.appInsights.trackException(err)
        throw new Error(err);
      }).finally(() => {
        // Undo the print styles & hide the spinner
        document.getElementById('app-body').removeAttribute("style");
        document.getElementById('main-content').removeAttribute("style");
        document.getElementById('sidebar-container').removeAttribute("style");
        this.spinnerService.hide();
      });
    });
  };
}
